import React from 'react';
import PropTypes from 'prop-types';
import styles from './IncidentErrorBoundary.module.css';
import { logError } from '../../../../utils/errorLogging';
import { trackModernUIInteraction } from '../../../../utils/modernUITracking';

/**
 * Error boundary for incident components
 * Catches rendering errors and provides recovery UI
 */
class IncidentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error with context
    logError('IncidentErrorBoundary', error, {
      componentStack: errorInfo.componentStack,
      incidentNumber: this.props.incidentNumber
    });

    // Track error occurrence
    // viewport is auto-enriched by trackModernUIInteraction — no need to pass explicitly
    trackModernUIInteraction('ModernUI.IncidentError.Occurred', {
      incidentId: this.props.incidentNumber || 'unknown',
      errorMessage: error.message,
      errorType: error.name,
    });

    this.setState({ errorInfo });
  }

  handleReset = () => {
    // Track retry button click
    trackModernUIInteraction('ModernUI.IncidentError.Retry', {
      incidentId: this.props.incidentNumber || 'unknown',
    });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h3 className={styles.errorTitle}>Unable to Display Incident</h3>
          <p className={styles.errorMessage}>
            An error occurred while rendering this incident.
          </p>
          <button
            className={styles.retryButton}
            onClick={this.handleReset}
            type="button"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

IncidentErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  incidentNumber: PropTypes.string,
  onReset: PropTypes.func
};

IncidentErrorBoundary.defaultProps = {
  incidentNumber: null,
  onReset: null
};

export default IncidentErrorBoundary;
